import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../../lib/api';
import { invitesApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { AuthLayout } from '../auth/AuthLayout';
import { restoreSession, useSession } from '../auth/session';
import { addJoinedServer, describeExpiry, inviteErrorText } from './inviteCode';
import { InvitePreview } from './InvitePreview';
import './servers.css';

/**
 * Öffentliche Einladungsseite `/invite/:code`: Vorschau ohne Anmeldung; Beitreten erfordert eine Sitzung
 * (sonst Login/Registrierung mit Rücksprung `?next=/invite/<code>`, siehe AuthGate/safeNext).
 */
export function InviteScreen() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const status = useSession((s) => s.status);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status === 'unknown') void restoreSession();
  }, [status]);

  const q = useQuery({ queryKey: qk.invite(code), queryFn: () => invitesApi.get(code), enabled: !!code, retry: false });
  const invite = q.data;
  const isMember = useAppStore((s) => (invite ? !!s.servers[invite.server.id] : false));
  const next = `/invite/${encodeURIComponent(code)}`;

  const join = async () => {
    if (!invite) return;
    if (isMember) {
      void navigate(paths.server(invite.server.id));
      return;
    }
    setBusy(true);
    setJoinError(null);
    try {
      const full = await invitesApi.accept(invite.code);
      addJoinedServer(full);
      void navigate(paths.server(full.id), { replace: true });
    } catch (e) {
      setJoinError(inviteErrorText(e));
      setBusy(false);
    }
  };

  if (q.isPending && code) {
    return (
      <AuthLayout title="Einladung wird geladen …" testId="invite-screen">
        <div className="empty">
          <span className="spinner" aria-label="Lädt" />
        </div>
      </AuthLayout>
    );
  }

  if (!invite) {
    const invalid = !code || (q.error instanceof ApiError && (q.error.code === 'INVITE_INVALID' || q.error.code === 'NOT_FOUND'));
    return (
      <AuthLayout title={invalid ? 'Einladung ungültig' : 'Einladung nicht verfügbar'} testId="invite-screen">
        <div className="stack" data-testid={invalid ? 'invite-invalid' : 'invite-error'}>
          <p className="muted" role="alert" style={{ textAlign: 'center', margin: 0 }}>
            {invalid ? 'Diese Einladung ist ungültig, abgelaufen oder wurde bereits zu oft benutzt. Frag nach einem neuen Link.' : inviteErrorText(q.error)}
          </p>
          <Link className="btn btn-primary btn-block" to={status === 'authenticated' ? paths.home() : paths.login()} data-testid="invite-continue">
            {status === 'authenticated' ? 'Weiter zur App' : 'Zur Anmeldung'}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const checking = status === 'unknown' || status === 'checking';
  return (
    <AuthLayout title="Einladung ans Feuer" testId="invite-screen">
      <div className="stack">
        <InvitePreview invite={invite} />
        {invite.creator && (
          <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>
            Eingeladen von {invite.creator.displayName || invite.creator.username}
            {!invite.isVanity && ` · ${describeExpiry(invite.expiresAt)}`}
          </p>
        )}
        {joinError && (
          <p className="error-text" role="alert" data-testid="invite-join-error">
            {joinError}
          </p>
        )}
        {checking ? (
          <button className="btn btn-primary btn-block" disabled>
            Sitzung wird geprüft …
          </button>
        ) : status === 'authenticated' ? (
          <button className="btn btn-primary btn-block" onClick={() => void join()} disabled={busy} data-testid="invite-accept">
            {isMember ? 'Server öffnen' : busy ? 'Beitreten …' : 'Einladung annehmen'}
          </button>
        ) : (
          <>
            <Link className="btn btn-primary btn-block" to={`${paths.login()}?next=${encodeURIComponent(next)}`} data-testid="invite-login">
              Anmelden und beitreten
            </Link>
            <Link className="btn btn-ghost btn-block" to={`/register?next=${encodeURIComponent(next)}`} data-testid="invite-register">
              Neu hier? Konto erstellen
            </Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
